import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {HttpClient} from '@angular/common/http';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';
import {TranslationTrackerService} from '../../services/translation-tracker/translation-tracker.service';
import {TranslationOverrideService, TranslationOverrideByKey} from '../../services/translation-override/translation-override.service';
import {LangSrcService} from '../../services/lang-src/lang-src.service';
import {LangSrc} from '../../interfaces/lang-src';
import {LangTgtService} from '../../services/lang-tgt/lang-tgt.service';
import {ContentContextService, ContentContext, ContentContextType, ContentItem} from '../../services/content-context/content-context.service';
import {GameContentTranslationService} from '../../services/game-content-translation/game-content-translation.service';
import {SectionContentTranslationService} from '../../services/section-content-translation/section-content-translation.service';

type CellState = 'idle' | 'saving' | 'saved' | 'error';
type TabId = 'i18n' | 'content';

interface ContentTypeConfig {
  fields: string[];
  fieldLabels: Record<string, string>;
  tabLabel: (items: ContentItem[]) => string;
  multiItem: boolean; // true = rows are items, false = rows are fields
}

const CONTENT_REGISTRY: Record<ContentContextType, ContentTypeConfig> = {
  game: {
    fields: ['name', 'description'],
    fieldLabels: {name: 'Nom du jeu', description: 'Énoncé'},
    tabLabel: items => items[0]?.name ?? 'Contenu jeu',
    multiItem: false,
  },
  section: {
    fields: ['name'],
    fieldLabels: {name: 'Nom de la section'},
    tabLabel: items => items[0]?.name ?? 'Contenu section',
    multiItem: false,
  },
  sections: {
    fields: ['name'],
    fieldLabels: {name: 'Nom'},
    tabLabel: items => `Sections (${items.length})`,
    multiItem: true,
  },
};

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  selector: 'app-admin-toolbar',
  templateUrl: './admin-toolbar.component.html',
  styleUrls: ['./admin-toolbar.component.scss']
})
export class AdminToolbarComponent implements OnInit {

  isOpen = false;
  activeTab: TabId = 'i18n';

  // ── i18n tab ────────────────────────────────────────────────────────────────
  loading = false;
  keys: string[] = [];
  langSrcs: LangSrc[] = [];
  search = '';
  values: { [key: string]: { [shortCode: string]: string } } = {};
  overrides: { [key: string]: TranslationOverrideByKey } = {};
  cellState: { [key: string]: { [shortCode: string]: CellState } } = {};
  private rawJsons: { [shortCode: string]: { [key: string]: string } } = {};

  // ── content tab (toutes entités) ─────────────────────────────────────────────
  context: ContentContext | null = null;
  contentLoading = false;
  // data[itemId][langCode][field] = value
  contentData: { [itemId: string]: { [langCode: string]: { [field: string]: string } } } = {};
  contentStates: { [itemId: string]: { [langCode: string]: CellState } } = {};

  constructor(
    public tracker: TranslationTrackerService,
    public langSrcSrv: LangSrcService,
    public router: Router,
    private overrideSrv: TranslationOverrideService,
    private translate: TranslateService,
    private http: HttpClient,
    private langTgtSrv: LangTgtService,
    private contentContextSrv: ContentContextService,
    private gameContentSrv: GameContentTranslationService,
    private sectionContentSrv: SectionContentTranslationService,
  ) {}

  ngOnInit(): void {
    this.tracker.keys$.subscribe(keys => this.keys = keys);
    this.langSrcs = (this.langSrcSrv.langs || []).slice();

    this.contentContextSrv.context$.subscribe(ctx => {
      this.context = ctx;
      this.contentData = {};
      this.contentStates = {};
      if (this.isOpen && ctx) {
        this.activeTab = 'content';
        this.loadContentData();
      }
    });
  }

  // ── Getters ──────────────────────────────────────────────────────────────────

  get filteredKeys(): string[] {
    const s = this.search.trim().toLowerCase();
    if (!s) { return this.keys; }
    return this.keys.filter(k => k.toLowerCase().includes(s));
  }

  get overrideCount(): number {
    let n = 0;
    for (const k of Object.keys(this.overrides)) {
      n += Object.keys(this.overrides[k] || {}).length;
    }
    return n;
  }

  // LangSrcs filtrés pour les tabs de contenu : exclut la langue cible active
  get contentLangSrcs(): LangSrc[] {
    const tgtCode = this.langTgtSrv.current?.code;
    if (!tgtCode) { return this.langSrcs; }
    return this.langSrcs.filter(l => l.shortCode !== tgtCode);
  }

  get currentConfig(): ContentTypeConfig | null {
    return this.context ? CONTENT_REGISTRY[this.context.type] : null;
  }

  get contentTabLabel(): string {
    if (!this.context) { return 'Contenu'; }
    return CONTENT_REGISTRY[this.context.type].tabLabel(this.context.items);
  }

  // ── Navigation ───────────────────────────────────────────────────────────────

  async toggle(): Promise<void> {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      if (this.context) {
        this.activeTab = 'content';
        await this.loadContentData();
      } else {
        this.activeTab = 'i18n';
        await this.loadAll();
      }
    }
  }

  close(): void { this.isOpen = false; }

  async switchTab(tab: TabId): Promise<void> {
    if (this.activeTab === tab) { return; }
    this.activeTab = tab;
    if (tab === 'content' && this.context && Object.keys(this.contentData).length === 0) {
      await this.loadContentData();
    }
    if (tab === 'i18n' && Object.keys(this.values).length === 0) {
      await this.loadAll();
    }
  }

  trackByKey(_index: number, key: string): string { return key; }
  trackByField(_index: number, field: string): string { return field; }
  trackByItemId(_index: number, item: ContentItem): string { return item.id; }

  autoGrow(el: HTMLTextAreaElement): void {
    const row = el.closest('tr');
    if (!row) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 200) + 'px';
      return;
    }
    const textareas = Array.from(row.querySelectorAll('textarea')) as HTMLTextAreaElement[];
    textareas.forEach(t => { t.style.height = 'auto'; });
    let max = 30;
    for (const t of textareas) {
      if (t.scrollHeight > max) { max = t.scrollHeight; }
    }
    const finalHeight = Math.min(max, 200);
    textareas.forEach(t => { t.style.height = finalHeight + 'px'; });
  }

  async onCellBlur(key: string, shortCode: string, el: HTMLTextAreaElement): Promise<void> {
    await this.saveCell(key, shortCode);
    this.shrinkRowOnBlur(el);
  }

  async onContentBlur(itemId: string, langCode: string, field: string, el: HTMLTextAreaElement): Promise<void> {
    await this.saveContentCell(itemId, langCode, field);
    this.shrinkRowOnBlur(el);
  }

  private shrinkRowOnBlur(el: HTMLTextAreaElement): void {
    setTimeout(() => {
      const row = el.closest('tr');
      if (!row) { return; }
      if (document.activeElement && row.contains(document.activeElement)) { return; }
      const textareas = Array.from(row.querySelectorAll('textarea')) as HTMLTextAreaElement[];
      textareas.forEach(t => {
        t.style.height = 'auto';
        t.style.height = Math.min(t.scrollHeight, 200) + 'px';
      });
    }, 0);
  }

  hasOverride(key: string, shortCode: string): boolean {
    return !!this.overrides[key]?.[shortCode];
  }

  // ── i18n tab ────────────────────────────────────────────────────────────────

  private async loadAll(): Promise<void> {
    this.loading = true;
    this.overrides = {};
    try {
      await Promise.all(this.langSrcs.map(async l => {
        if (this.rawJsons[l.shortCode]) { return; }
        const data = await this.http.get<{ [k: string]: string }>(`assets/i18n/${l.shortCode}.json`).toPromise();
        this.rawJsons[l.shortCode] = data ?? {};
      }));
    } catch (e) {
      console.error('AdminToolbar: failed to load raw JSONs', e);
    }
    try {
      const results = await Promise.all(
        this.keys.map(async k => ({key: k, data: await this.overrideSrv.getByKey(k)}))
      );
      for (const {key, data} of results) { this.overrides[key] = data; }
    } catch (e) {
      console.error('AdminToolbar: failed to load overrides', e);
    }
    for (const key of this.keys) {
      this.values[key] = {};
      this.cellState[key] = {};
      for (const lang of this.langSrcs) {
        const code = lang.shortCode;
        const override = this.overrides[key]?.[code];
        this.values[key][code] = override ? override.value : (this.rawJsons[code]?.[key] ?? '');
        this.cellState[key][code] = 'idle';
      }
    }
    this.loading = false;
  }

  async saveCell(key: string, shortCode: string): Promise<void> {
    const value = this.values[key]?.[shortCode] ?? '';
    const existing = this.overrides[key]?.[shortCode];
    if (existing && existing.value === value) { return; }
    if (!existing && value === (this.rawJsons[shortCode]?.[key] ?? '')) { return; }
    this.cellState[key][shortCode] = 'saving';
    try {
      const result = await this.overrideSrv.upsert(key, shortCode, value);
      if (!this.overrides[key]) { this.overrides[key] = {}; }
      this.overrides[key][shortCode] = {id: result.id, value: result.value};
      this.translate.setTranslation(shortCode, {[key]: value}, true);
      this.cellState[key][shortCode] = 'saved';
      setTimeout(() => {
        if (this.cellState[key]?.[shortCode] === 'saved') { this.cellState[key][shortCode] = 'idle'; }
      }, 1500);
    } catch (e) {
      console.error('AdminToolbar: save failed', e);
      this.cellState[key][shortCode] = 'error';
    }
  }

  async deleteCell(key: string, shortCode: string): Promise<void> {
    const existing = this.overrides[key]?.[shortCode];
    if (!existing) { return; }
    if (!confirm('Supprimer cette override ? Rechargez la page après pour voir la valeur JSON.')) { return; }
    this.cellState[key][shortCode] = 'saving';
    try {
      await this.overrideSrv.delete(existing.id);
      if (this.overrides[key]) { delete this.overrides[key][shortCode]; }
      this.cellState[key][shortCode] = 'idle';
    } catch (e) {
      console.error('AdminToolbar: delete failed', e);
      this.cellState[key][shortCode] = 'error';
    }
  }

  // ── content tab ─────────────────────────────────────────────────────────────

  async loadContentData(): Promise<void> {
    if (!this.context) { return; }
    this.contentLoading = true;
    const config = CONTENT_REGISTRY[this.context.type];
    try {
      await Promise.all(this.context.items.map(async item => {
        const raw = await this.fetchTranslations(this.context!.type, item.id);
        this.contentData[item.id] = {};
        this.contentStates[item.id] = {};
        for (const lang of this.contentLangSrcs) {
          this.contentData[item.id][lang.shortCode] = {};
          this.contentStates[item.id][lang.shortCode] = 'idle';
          for (const field of config.fields) {
            this.contentData[item.id][lang.shortCode][field] = raw[lang.shortCode]?.[field] ?? '';
          }
        }
      }));
    } catch (e) {
      console.error('AdminToolbar: failed to load content translations', e);
    }
    this.contentLoading = false;
  }

  private async fetchTranslations(type: ContentContextType, itemId: string): Promise<Record<string, any>> {
    switch (type) {
      case 'game':
        return await this.gameContentSrv.getAll(itemId);
      case 'section':
      case 'sections':
        return await this.sectionContentSrv.getAll(itemId);
    }
  }

  async saveContentCell(itemId: string, langCode: string, field: string): Promise<void> {
    if (!this.context) { return; }
    const value = this.contentData[itemId]?.[langCode]?.[field] ?? '';
    this.contentStates[itemId][langCode] = 'saving';
    try {
      await this.pushTranslation(this.context.type, itemId, langCode, field, value);
      this.contentStates[itemId][langCode] = 'saved';
      setTimeout(() => {
        if (this.contentStates[itemId]?.[langCode] === 'saved') { this.contentStates[itemId][langCode] = 'idle'; }
      }, 1500);
    } catch (e) {
      console.error('AdminToolbar: content save failed', e);
      this.contentStates[itemId][langCode] = 'error';
    }
  }

  private async pushTranslation(type: ContentContextType, itemId: string, langCode: string, field: string, value: string): Promise<void> {
    switch (type) {
      case 'game':
        return await this.gameContentSrv.update(itemId, langCode, field as 'name' | 'description', value);
      case 'section':
      case 'sections':
        return await this.sectionContentSrv.update(itemId, langCode, field as 'name', value);
    }
  }
}
